import { useCallback } from "react";
import { MessageType } from "../lib/store/reducers/responseMessagesReducer";

export type RestockMessage = {
  type: MessageType;
  title: string;
  content: string;
};

export type RestockApiResult<TSuccessMeta = unknown> =
  | { ok: true; meta?: TSuccessMeta }
  | { ok: false; reason: "INVALID_LOCATION"; requested?: string; suggested?: string | null }
  | { ok: false; reason: "UNKNOWN"; message?: string };

export type UseRestockWorkflowConfig<TItem, TItemsContext, TPayload, TApiSuccessMeta = unknown> = {
  notify: (msg: RestockMessage) => void;
  items: TItem[];
  selectedItemId: number | string | null;
  setSelectedItemId: (id: number | string | null) => void;
  activeDeliveryId: number;
  setActiveDeliveryId?: (id: number | string) => void;
  findItemByScan: (items: TItem[], scannedBarcode: string) => TItem | undefined;
  getItemId: (item: TItem) => number | string;
  isVerificationSatisfied: (item: TItem) => boolean;
  incrementVerified: (item: TItem) => TItem;
  upsertItem: (item: TItem) => void;
  formatLocationBarcode: (raw: string) => string;
  isValidLocationBarcode: (formatted: string) => boolean;
  invalidLocationMessage: string;
  buildPayload: (args: {
    item: TItem;
    formattedLocation: string;
    activeDeliveryId: number;
  }) => TPayload;

  restockApiCall: (payload: TPayload) => Promise<RestockApiResult<TApiSuccessMeta>>;

  onRestockSuccess: (args: {
    item: TItem;
    formattedLocation: string;
    apiMeta?: TApiSuccessMeta;
  }) => { updatedItem: TItem; clearSelection?: boolean; nextDeliveryId?: number | string };
};

export function useRestockWorkflow<TItem, TItemsContext, TPayload, TApiSuccessMeta = unknown>(
  config: UseRestockWorkflowConfig<TItem, TItemsContext, TPayload, TApiSuccessMeta>
) {
  const {
    notify,
    items,
    selectedItemId,
    setSelectedItemId,
    setActiveDeliveryId,
    findItemByScan,
    getItemId,
    isVerificationSatisfied,
    incrementVerified,
    upsertItem,
    formatLocationBarcode,
    isValidLocationBarcode,
    invalidLocationMessage,
    buildPayload,
    restockApiCall,
    onRestockSuccess,
  } = config;

  const scanItem = useCallback(
    async (
      scannedBarcode: string,
      messages?: { alreadySelected: string; notFound: string; maxReached: (code: string) => string }
    ) => {
      if (selectedItemId !== null && selectedItemId !== undefined) {
        notify({
          type: MessageType.WARNING,
          title: "Hoppsan!",
          content: messages?.alreadySelected ?? "Du har redan en vald artikel. Slutför den först.",
        });
        return;
      }

      const scannedItem = findItemByScan(items, scannedBarcode);

      if (!scannedItem) {
        notify({
          type: MessageType.WARNING,
          title: "Hoppsan!",
          content: messages?.notFound ?? "Artikeln finns inte i ordern.",
        });
        return;
      }

      if (isVerificationSatisfied(scannedItem)) {
        notify({
          type: MessageType.WARNING,
          title: "Hoppsan!",
          content:
            messages?.maxReached?.(scannedBarcode) ??
            `Max verifierat nått för '${scannedBarcode}'.`,
        });
        return;
      }

      const updated = incrementVerified(scannedItem);
      upsertItem(updated);
    },
    [
      selectedItemId,
      items,
      notify,
      findItemByScan,
      isVerificationSatisfied,
      incrementVerified,
      upsertItem,
    ]
  );

  const scanLocationAndRestock = useCallback(
    async (rawLocationBarcode: string) => {
      const formatted = formatLocationBarcode(rawLocationBarcode);

      if (!isValidLocationBarcode(formatted)) {
        notify({ type: MessageType.WARNING, title: "Hoppsan!", content: invalidLocationMessage });
        return;
      }

      const currentItem = items.find((i) => getItemId(i) === selectedItemId);
      if (!currentItem) return;

      const payload = buildPayload({
        item: currentItem,
        formattedLocation: formatted,
        activeDeliveryId: config.activeDeliveryId,
      });

      let result: RestockApiResult<TApiSuccessMeta>;
      try {
        result = await restockApiCall(payload);
      } catch (e: any) {
        notify({
          type: MessageType.WARNING,
          title: "Hoppsan!",
          content: e?.message ?? "Något gick fel..",
        });
        return;
      }

      if (!result.ok) {
        if (result.reason === "INVALID_LOCATION") {
          const suggested = result.suggested;
          if (!suggested) {
            notify({ type: MessageType.WARNING, title: "Hoppsan!", content: "Något gick fel.." });
            return;
          }
          notify({
            type: MessageType.WARNING,
            title: "Hoppsan!",
            content: `Ogiltig hylla '${result.requested}'. Systemet rekommenderar hylla '${suggested}'`,
          });
          return;
        }

        notify({
          type: MessageType.WARNING,
          title: "Hoppsan!",
          content: result.message ?? "Något gick fel..",
        });
        return;
      }

      const { updatedItem, clearSelection, nextDeliveryId } = onRestockSuccess({
        item: currentItem,
        formattedLocation: formatted,
        apiMeta: result.meta,
      });

      upsertItem(updatedItem);

      if (clearSelection) setSelectedItemId(null);
      if (nextDeliveryId !== undefined && nextDeliveryId !== null && setActiveDeliveryId) {
        setActiveDeliveryId(nextDeliveryId);
      }
    },
    [
      items,
      selectedItemId,
      notify,
      formatLocationBarcode,
      isValidLocationBarcode,
      invalidLocationMessage,
      getItemId,
      buildPayload,
      restockApiCall,
      onRestockSuccess,
      upsertItem,
      setSelectedItemId,
      setActiveDeliveryId,
      config.activeDeliveryId,
    ]
  );

  return {
    scanItem,
    scanLocationAndRestock,
  };
}
